import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { invalidateLookup, loadLookup, subscribeLookupChanges, type LookupKind } from './lookups';

// Lookup kinds we eagerly preload when a flow opens so that the node summaries
// and read-only displays can resolve Voice/Variable/Huntgroup/… ids to their
// human-readable names without each node making its own async roundtrip.
const PRELOAD_KINDS: LookupKind[] = [
  'variable',
  'audiofile',
  'huntgroup',
  'agent',
  'weekplan',
  'calendar',
  'flow',
  'application',
  // Needed by Data Lookup / Schedule Lookup so the node summary can resolve
  // simpleDbId -> dbName without each editor refetching the list.
  'database',
];

export type ResolverMaps = Partial<Record<LookupKind, Map<string, string>>>;

export const LookupCtx = createContext<ResolverMaps>({});

export function LookupProvider({
  applicationId,
  children,
}: {
  applicationId: number;
  children: ReactNode;
}) {
  const [maps, setMaps] = useState<ResolverMaps>({});
  const [version, setVersion] = useState(0);

  // Re-build the resolver maps when:
  //   - the applicationId changes (user switched apps),
  //   - the lookup cache was invalidated somewhere (variable created in
  //     the modal, or window focus / visibilitychange below).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next: ResolverMaps = {};
      await Promise.all(
        PRELOAD_KINDS.map(async (k) => {
          try {
            const items = await loadLookup(k, applicationId);
            const m = new Map<string, string>();
            for (const item of items) m.set(String(item.id), item.name);
            next[k] = m;
          } catch {
            // Lookup failure is non-fatal; the summary simply falls back to the id.
          }
        })
      );
      if (!cancelled) setMaps(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [applicationId, version]);

  // Anything in the SPA that calls `invalidateLookup` (e.g. the variable
  // create modal) bumps our `version` so the resolver maps re-load.
  useEffect(() => {
    return subscribeLookupChanges(() => setVersion((v) => v + 1));
  }, []);

  // Whenever the user switches back to this tab, force all flowbuilder
  // lookups to refresh. This matches the user's expectation that voice
  // files / weekplans added in another tab show up immediately when they
  // come back to the editor — no manual page refresh required.
  useEffect(() => {
    const refresh = () => {
      // Don't bother refreshing when the page is still hidden — we'll
      // pick this up the next time the tab becomes visible.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      invalidateLookup();
    };
    const onVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return <LookupCtx.Provider value={maps}>{children}</LookupCtx.Provider>;
}

// Resolve an id to a human name for a given lookup kind. Returns `null` if the
// lookup hasn't loaded yet or the id is unknown, so callers can fall back
// to rendering the raw id.
export function useLookupName(kind: LookupKind | null | undefined, id: string | null | undefined): string | null {
  const maps = useContext(LookupCtx);
  if (!kind || !id) return null;
  const m = maps[kind];
  if (!m) return null;
  return m.get(String(id)) ?? null;
}
